/**
 * Expandable Data Table Component
 *
 * MUI-based expandable data table with Card styling matching PendingInvitationsSection.
 * Supports row expansion, click handlers, and navigation.
 */

import { Fragment, type ReactNode, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTableScrollIndicators } from '@hooks/useTableScrollIndicators';
import { ArrowForward, ExpandLess, ExpandMore } from '@mui/icons-material';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Collapse,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

interface Column<T = Record<string, unknown>> {
  key: keyof T & string;
  label: string;
  render?: (value: T[keyof T], row: T) => ReactNode;
  align?: 'left' | 'center' | 'right';
}

interface ExpandableDataTableProps<T = Record<string, unknown>> {
  title: string;
  columns: Column<T>[];
  data: T[];
  total?: number;
  loading?: boolean;
  onRowClick?: (row: T, event?: React.MouseEvent) => void;
  getRowUrl?: (row: T) => string;
  onViewAll?: () => void;
  emptyMessage?: string;
  expandedRows: Set<string>;
  getRowId: (row: T) => string;
  renderExpandedContent: (row: T) => ReactNode;
  collapsible?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

export function ExpandableDataTable<T = Record<string, unknown>>({
  title,
  columns,
  data,
  total,
  loading = false,
  onRowClick,
  getRowUrl,
  onViewAll,
  emptyMessage = 'No data available',
  expandedRows,
  getRowId,
  renderExpandedContent,
  collapsible = false,
  defaultCollapsed = false,
  onCollapsedChange,
}: ExpandableDataTableProps<T>) {
  const showViewAll = total && total > data.length;
  const navigate = useNavigate();
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { showLeftIndicator, showRightIndicator } = useTableScrollIndicators(tableContainerRef);

  const handleCollapseToggle = () => {
    const newCollapsed = !isCollapsed;
    setIsCollapsed(newCollapsed);
    onCollapsedChange?.(newCollapsed);
  };

  const handleRowClick = (row: T, event?: React.MouseEvent) => {
    if (onRowClick) {
      onRowClick(row, event);
    } else if (getRowUrl) {
      const url = getRowUrl(row);
      if (url) {
        navigate(url);
      }
    }
  };

  const clickable = onRowClick || getRowUrl;

  return (
    <Card
      elevation={1}
      sx={{ borderRadius: 2, overflow: 'hidden', border: 1, borderColor: 'grey.200' }}
    >
      <CardHeader
        title={
          collapsible ? (
            <Button
              variant="text"
              onClick={handleCollapseToggle}
              startIcon={isCollapsed ? <ExpandMore /> : <ExpandLess />}
              sx={{
                justifyContent: 'flex-start',
                textTransform: 'none',
                color: 'text.primary',
                fontSize: '1rem',
                fontWeight: 600,
                p: 0,
                '&:hover': { bgcolor: 'transparent' },
              }}
            >
              {title}
            </Button>
          ) : (
            title
          )
        }
        subheader={!isCollapsed && total ? `Showing ${data.length} of ${total} total` : undefined}
        slotProps={{
          title: collapsible ? undefined : { variant: 'h6', fontWeight: 600 },
          subheader: { variant: 'body2' },
        }}
        action={
          <>
            {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
            {showViewAll && onViewAll && (
              <Button
                variant="text"
                onClick={onViewAll}
                size="small"
                endIcon={<ArrowForward fontSize="small" />}
                sx={{
                  color: 'primary.main',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  '&:hover': {
                    color: 'primary.dark',
                  },
                }}
              >
                See More
              </Button>
            )}
          </>
        }
      />
      <Collapse in={!isCollapsed}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {data.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                py: 6,
                px: 3,
              }}
            >
              {loading ? (
                <CircularProgress size={28} />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {emptyMessage}
                </Typography>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                overflow: 'hidden',
                position: 'relative',
              }}
            >
              <TableContainer ref={tableContainerRef}>
                <Table>
                  <TableHead>
                    <TableRow sx={{ borderBottom: 1, borderColor: 'divider' }}>
                      {columns.map(column => (
                        <TableCell
                          key={column.key}
                          align={column.align}
                          sx={{ bgcolor: 'grey.50', fontWeight: 600, fontSize: '0.875rem' }}
                        >
                          {column.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody
                    sx={{
                      '& tr:nth-last-of-type(2) td': {
                        borderBottom: 'none',
                      },
                    }}
                  >
                    {data.map((row) => {
                      const rowId = getRowId(row);
                      const isExpanded = expandedRows.has(rowId);

                      return (
                        <Fragment key={rowId}>
                          <TableRow
                            sx={{
                              ...(clickable && {
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: 'action.hover',
                                },
                              }),
                            }}
                            onClick={clickable ? e => handleRowClick(row, e) : undefined}
                          >
                            {columns.map(column => (
                              <TableCell key={column.key} align={column.align}>
                                {column.render
                                  ? column.render(row[column.key], row)
                                  : String(row[column.key] || '')}
                              </TableCell>
                            ))}
                          </TableRow>
                          <TableRow>
                            <TableCell
                              colSpan={columns.length}
                              sx={{ p: 0, borderBottom: 'none' }}
                            >
                              <Collapse in={isExpanded} unmountOnExit>
                                <Box
                                  sx={{
                                    bgcolor: 'grey.50',
                                    p: 2,
                                    borderTop: '1px solid',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                  }}
                                >
                                  {renderExpandedContent(row)}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {showLeftIndicator && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    bottom: 0,
                    width: 32,
                    pointerEvents: 'none',
                    background: theme =>
                      `linear-gradient(to right, ${theme.palette.grey[200]}, transparent)`,
                  }}
                />
              )}
              {showRightIndicator && (
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    bottom: 0,
                    width: 32,
                    pointerEvents: 'none',
                    background: theme =>
                      `linear-gradient(to left, ${theme.palette.grey[200]}, transparent)`,
                  }}
                />
              )}
            </Box>
          )}
        </CardContent>
      </Collapse>
    </Card>
  );
}
